import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';

const EMPTY = {
  idEmpleado: 0,
  numeroLicencia: '',
  claseLicencia: '',
  venceLicencia: '',
  observaciones: '',
  activo: true, // 👈 se mantiene en el payload, pero ya no se muestra en UI
};

// Endpoints + modelos para armar el combo de Empleados
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointCorreo = ENDPOINTS.correos;
const modeloCorreo = Models.correos;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idEmpleado: '',
    numeroLicencia: '',
    claseLicencia: '',
    venceLicencia: '',
    observaciones: '',
  });

  // catálogo de empleados enriquecidos (nombre completo + correo)
  const [empleados, setEmpleados] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [errorEmps, setErrorEmps] = useState(null);

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idEmpleado':
        if (!value || Number(value) <= 0) error = 'Selecciona un empleado.';
        break;
      case 'numeroLicencia':
        if (!value) error = 'El número de licencia es obligatorio.';
        break;
      case 'claseLicencia':
        if (!value) error = 'La clase de licencia es obligatoria.';
        break;
      case 'venceLicencia':
        // if (!value) error = 'La fecha de vencimiento es obligatoria.';
        break;
      case 'observaciones':
        if (!value) error = 'Las observaciones son obligatorias.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['idEmpleado', 'numeroLicencia', 'claseLicencia', 'venceLicencia', 'observaciones'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  // Cargar datos de edición
  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idChofer: data.idChofer ?? 0,
        idEmpleado: data.idEmpleado ?? 0,
        numeroLicencia: data.numeroLicencia ?? '',
        claseLicencia: data.claseLicencia ?? '',
        venceLicencia: (data.venceLicencia ?? '').slice(0, 10),
        observaciones: data.observaciones ?? '',
        activo: !!data.activo,
      };
      setForm(filled);
      validateField('idEmpleado', filled.idEmpleado);
      validateField('numeroLicencia', filled.numeroLicencia);
      validateField('claseLicencia', filled.claseLicencia);
      validateField('venceLicencia', filled.venceLicencia);
      validateField('observaciones', filled.observaciones);
    } else {
      setForm(EMPTY);
      setErrors({ idEmpleado: '', numeroLicencia: '', claseLicencia: '', venceLicencia: '', observaciones: '' });
    }
  }, [mode, data]);

  // Cargar empleados + personas + correos cuando se abre el modal
  useEffect(() => {
    if (!open) return;

    let canceled = false;

    async function loadEmpleados() {
      setLoadingEmps(true);
      setErrorEmps(null);

      try {
        const [respEmps, respPers, respCors, respChoferes] = await Promise.all([
          axios.get(endpointEmpleado),
          axios.get(endpointPersona),
          axios.get(endpointCorreo),
          axios.get(ENDPOINTS.chofer),
        ]);

        let empleadosNorm = (respEmps.data || []).map(modeloEmpleado);
        let personasNorm = (respPers.data || []).map(modeloPersona);
        let correosNorm = (respCors.data || []).map(modeloCorreo);

        const sessionEmpresaId = getSessionEmpresaId();
        const isSuperAdmin = isSessionSuperAdmin();

        // Filtrar por empresa si no es superadmin
        if (!isSuperAdmin) {
          empleadosNorm = empleadosNorm.filter(e => e.idEmpresa === sessionEmpresaId);
          // Opcional: filtrar personas y correos correlacionados para optimizar memoria
          const validPersonaIds = new Set(empleadosNorm.map(e => e.idPersona));
          personasNorm = personasNorm.filter(p => validPersonaIds.has(p.idPersona));
          correosNorm = correosNorm.filter(c => validPersonaIds.has(c.idPersona));
        }

        // Enriquecer empleados con nombre completo y correo
        empleadosNorm = empleadosNorm.map(emp => {
          const per = personasNorm.find(p => p.idPersona === emp.idPersona);
          const cor = per
            ? correosNorm.find(c => c.idPersona === per.idPersona)
            : null;

          const fullName = per
            ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
            : '';

          return {
            ...emp,
            nombreCompleto: fullName || `Empleado #${emp.idEmpleado}`,
            correo: cor?.correo || '',
          };
        });

        // Filtrar empleados que ya son choferes (excepto el actual en edición)
        const choferesExistentes = (respChoferes.data || []).map(Models.chofer);
        empleadosNorm = empleadosNorm.filter(emp => {
          // En edición, permitir el empleado actual
          if (mode === 'edit' && data && emp.idEmpleado === data.idEmpleado) {
            return true;
          }
          // Excluir si ya existe en choferes
          return !choferesExistentes.some(c => c.idEmpleado === emp.idEmpleado);
        });

        if (!canceled) {
          setEmpleados(empleadosNorm);
        }
      } catch (err) {
        console.error('Error cargando empleados/personas/correos', err);
        if (!canceled) {
          setErrorEmps('No se pudieron cargar los empleados.');
        }
      } finally {
        if (!canceled) {
          setLoadingEmps(false);
        }
      }
    }

    loadEmpleados();

    return () => {
      canceled = true;
    };
  }, [open]);

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled =
    !form.idEmpleado ||
    !form.numeroLicencia?.trim() ||
    !form.claseLicencia?.trim() ||
    // !form.venceLicencia || // 👈 Ahora opcional
    !form.observaciones?.trim() ||
    hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo chofer' : 'Editar chofer'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Empleado (select con nombre completo) */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empleado:</label>
        <br /><br />
        <select
          className={`infoField${errors.idEmpleado ? ' infoField--error' : ''}`}
          value={form.idEmpleado || 0}
          onChange={(e) => handleChange('idEmpleado', Number(e.target.value) || 0)}
        >
          <option value={0} disabled>
            {loadingEmps ? 'Cargando empleados…' : 'Seleccione un empleado…'}
          </option>
          {empleados.map(emp => {
            // Mostrar solo el nombre completo
            const label = emp.nombreCompleto || `Empleado sin nombre`;

            return (
              <option key={emp.idEmpleado} value={emp.idEmpleado}>
                {label}
              </option>
            );
          })}
        </select>
        {errors.idEmpleado && <small style={errorStyle}>{errors.idEmpleado}</small>}
        {errorEmps && (
          <p
            style={{
              color: '#b91c1c',
              fontSize: '.8rem',
              marginTop: '.25rem',
            }}
          >
            {errorEmps}
          </p>
        )}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>No. Licencia:</label>
        <br /><br />
        <input
          className={`infoField${errors.numeroLicencia ? ' infoField--error' : ''}`}
          value={form.numeroLicencia}
          onChange={(e) => handleChange('numeroLicencia', e.target.value)}
          maxLength={50}
        />
        {errors.numeroLicencia && <small style={errorStyle}>{errors.numeroLicencia}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Clase de licencia:</label>
        <br /><br />
        <input
          className={`infoField${errors.claseLicencia ? ' infoField--error' : ''}`}
          value={form.claseLicencia}
          onChange={(e) => handleChange('claseLicencia', e.target.value)}
          maxLength={50}
        />
        {errors.claseLicencia && <small style={errorStyle}>{errors.claseLicencia}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Vence licencia:</label>
        <br /><br />
        <div style={{ display: 'flex', width: '75%' }}>
          <input
            className={`infoField${errors.venceLicencia ? ' infoField--error' : ''}`}
            type="date"
            style={{
              flex: 1,
              width: 'auto',
              minWidth: 0,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              zIndex: 1
            }}
            onKeyDown={(e) => e.preventDefault()}
            value={form.venceLicencia || ''}
            onChange={(e) => handleChange('venceLicencia', e.target.value)}
            onBlur={(e) => validateField('venceLicencia', e.target.value)}
          />
          <button
            type="button"
            className="roles-btn"
            style={{
              padding: '0 12px',
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              marginLeft: '-1px',
              flexShrink: 0
            }}
            onClick={() => handleChange('venceLicencia', '')}
          >
            Limpiar
          </button>
        </div>
        {errors.venceLicencia && <small style={errorStyle}>{errors.venceLicencia}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Observaciones:</label>
        <br /><br />
        <textarea
          className={`infoField${errors.observaciones ? ' infoField--error' : ''}`}
          value={form.observaciones}
          onChange={(e) => handleChange('observaciones', e.target.value)}
          rows={3}
          maxLength={500}
        />
        {errors.observaciones && <small style={errorStyle}>{errors.observaciones}</small>}
      </div>

      {/* 👇 "Activo" se conserva en form pero ya no se muestra en la UI
      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          <input
            type="checkbox"
            checked={form.activo}
            onChange={(e) => setForm({ ...form, activo: e.target.checked })}
            style={{ marginRight: '.5rem' }}
          />
          Activo
        </label>
      </div>
      */}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
